import type { HazardEvent } from "@eldrun/protocol";
import { GLOW_POOL } from "./smoke.ts";

/**
 * Fontes de chama (F93): áreas que soltam lambidas enquanto duram, no pool de chamas do `Smoke`. O fogo e as
 * brasas dos perigos (a faixa ou o disco do evento) e o Círculo da Fênix (a borda do círculo). Só desenho do
 * cliente: o servidor decide o dano. Sem cena (os testes leem as lambidas por um `FlameSink`). Determinístico (os pontos seguem uma sequência de baixa discrepância por
 * fonte, sem `Math.random`) e sem alocar por quadro.
 */

/** A forma da fonte: faixa (rastro de fogo), disco (brasas, fogo parado) ou anel (a borda da Fênix). */
export type FlameShape = "faixa" | "disco" | "anel";

export interface FlameSource {
  id: string;
  shape: FlameShape;
  x: number;
  z: number;
  /** Fim da faixa (as outras formas ignoram). */
  x2: number;
  z2: number;
  /** Meia-largura da faixa ou raio do disco e do anel (m). */
  radius: number;
  /** Altura do chão quando não é o piso do ponto (a laje da Caverna). */
  y?: number;
  /** Começo e fim (ms, relógio do cliente). */
  start: number;
  end: number;
  /** Para de soltar nesta fração da vida (padrão `STOP_AT`: o desenho do perigo some no último quarto). */
  stop?: number;
  color: string;
  /** Lambidas por segundo e a força (os efeitos dos aliados vêm mais fracos). */
  rate: number;
  alpha: number;
  /** Largura de cada língua (m). */
  size: number;
}

/** Quem recebe as lambidas (o `Smoke`). */
export interface FlameSink {
  flames(x: number, y: number, z: number, color: string, n?: number, spread?: number, size?: number, alpha?: number): void;
  /** Chamas e magia vivas no pool. */
  readonly glowLive: number;
}

/** Teto de fontes ao mesmo tempo: com o teto cheio, a mais longe da câmera cede o lugar. */
export const FLAME_SOURCES = 24;
/** Só solta com a câmera até esta distância da fonte (m, no chão). */
export const FLAME_RANGE = 40;
/** Para de soltar nesta fração da vida (o perigo some de 0,75 a 1). */
export const STOP_AT = 0.8;
/** Com o pool de chamas passando desta fração, as fontes soltam pela metade (os impactos ficam com o resto). */
const BUSY = 0.6;
/** Teto de lambidas por segundo de uma fonte, qualquer que seja o tamanho dela. */
export const MAX_RATE = 10;

/** Lambidas por segundo de um perigo pelo tamanho: o disco pela área, a faixa pelo comprimento (m). */
export function hazardRate(shape: FlameShape, radius: number, length: number): number {
  return Math.min(MAX_RATE, shape === "faixa" ? 1.2 * Math.max(1, length) : 2 + 0.5 * radius * radius);
}

/** A largura das línguas dos perigos (m): a faixa é mais estreita que o disco. */
export const HAZARD_FLAME = { faixa: 0.7, disco: 0.8 } as const;
/** A borda do Círculo da Fênix: lambidas por segundo por metro de borda, o teto e a largura. */
export const FENIX_FLAME = { perMeter: 1.2, max: 36, size: 0.7 } as const;

/** O fogo e as brasas de um perigo (`fogo` com dois pontos é a faixa; o resto, o disco), na cor dada. */
export function hazardSource(e: HazardEvent, now: number, color: string): FlameSource {
  const shape: FlameShape = e.kind === "fogo" && (e.x !== e.x2 || e.z !== e.z2) ? "faixa" : "disco";
  return { id: e.id, shape, x: e.x, z: e.z, x2: e.x2, z2: e.z2, radius: e.radius, ...(e.y !== undefined ? { y: e.y } : {}), start: now, end: now + e.ms, color, rate: hazardRate(shape, e.radius, Math.hypot(e.x2 - e.x, e.z2 - e.z)), alpha: 1, size: HAZARD_FLAME[shape] };
}

/**
 * A borda do Círculo da Fênix (`x, z, radius, ms` do evento), na cor e na força de quem usou: a taxa pelo
 * comprimento da borda (no raio de 7 m, o teto, 36 por segundo e uns 25 vivos). Solta até perto do fim.
 */
export function fenixSource(id: string, e: { x: number; z: number; radius: number; ms: number }, now: number, color: string, alpha: number): FlameSource {
  const rate = Math.min(FENIX_FLAME.max, FENIX_FLAME.perMeter * 2 * Math.PI * e.radius);
  return { id, shape: "anel", x: e.x, z: e.z, x2: e.x, z2: e.z, radius: e.radius, start: now, end: now + e.ms, stop: 0.95, color, rate, alpha, size: FENIX_FLAME.size };
}

interface Live extends FlameSource {
  /** Lambidas devidas (fração acumulada) e quantas já saíram (o índice da sequência). */
  owed: number;
  k: number;
}

/** A sequência R2 (Roberts): pontos bem espalhados no quadrado unitário, um por índice. */
const R2 = [0.7548776662466927, 0.5698402909980532] as const;

/** O ponto `k` da fonte, no chão (x, z), escrito em `out`. */
export function sourcePoint(s: Pick<FlameSource, "shape" | "x" | "z" | "x2" | "z2" | "radius">, k: number, out: { x: number; z: number }): void {
  const u = (0.5 + k * R2[0]) % 1;
  const v = (0.5 + k * R2[1]) % 1;
  if (s.shape === "faixa") {
    // Ao longo do segmento e de través, mais no meio da faixa (como o fogo do desenho).
    const dx = s.x2 - s.x;
    const dz = s.z2 - s.z;
    const len = Math.hypot(dx, dz) || 1;
    const side = (v - 0.5) * 2 * s.radius * 0.7;
    out.x = s.x + dx * u + (-dz / len) * side;
    out.z = s.z + dz * u + (dx / len) * side;
    return;
  }
  const a = u * Math.PI * 2;
  // O disco espalhado por igual na área; o anel numa coroa estreita na borda.
  const r = s.shape === "disco" ? s.radius * 0.85 * Math.sqrt(v) : s.radius * (0.9 + 0.1 * v);
  out.x = s.x + Math.cos(a) * r;
  out.z = s.z + Math.sin(a) * r;
}

/** As fontes vivas: `add` na chegada, `remove` quando o servidor apaga, `update` a cada quadro. */
export class FlameEmitters {
  private readonly sink: FlameSink;
  private readonly live: Live[] = [];
  private readonly point = { x: 0, z: 0 };
  /** A câmera do último quadro (quem sai com o teto cheio é a fonte mais longe dela). */
  private camX = 0;
  private camZ = 0;
  /** Lambidas soltas desde o começo (diagnóstico). */
  emitted = 0;

  constructor(sink: FlameSink) {
    this.sink = sink;
  }

  get count(): number {
    return this.live.length;
  }

  add(source: FlameSource): void {
    if (source.rate <= 0 || source.alpha <= 0 || source.end <= source.start) return;
    this.remove(source.id);
    const next: Live = { ...source, owed: 0, k: 0 };
    if (this.live.length >= FLAME_SOURCES) {
      // Sai a mais longe da câmera; se a nova é a mais longe, ela nem entra.
      let far = -1;
      let farDistance = distanceTo(next, this.camX, this.camZ);
      for (let i = 0; i < this.live.length; i++) {
        const d = distanceTo(this.live[i] as Live, this.camX, this.camZ);
        if (d > farDistance) {
          far = i;
          farDistance = d;
        }
      }
      if (far < 0) return;
      this.live.splice(far, 1);
    }
    this.live.push(next);
  }

  remove(id: string): void {
    for (let i = this.live.length - 1; i >= 0; i--) if ((this.live[i] as Live).id === id) this.live.splice(i, 1);
  }

  /**
   * Solta as lambidas devidas no quadro: `rate × dt` por fonte (no máximo as de 0,1 s, e pelo menos 3, por
   * quadro: sem rajada depois de um quadro travado), só com a câmera em (cx, cz) até `FLAME_RANGE`.
   * `ground(x, z)`: o piso no ponto.
   */
  update(now: number, dt: number, cx: number, cz: number, ground: (x: number, z: number) => number): void {
    this.camX = cx;
    this.camZ = cz;
    const share = this.sink.glowLive > BUSY * GLOW_POOL ? 0.5 : 1;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const s = this.live[i] as Live;
      if (now >= s.end) {
        this.live.splice(i, 1);
        continue;
      }
      if (now < s.start || (now - s.start) / (s.end - s.start) >= (s.stop ?? STOP_AT)) continue;
      // A distância até o ponto mais perto da forma (a faixa pelo segmento).
      if (distanceTo(s, cx, cz) > FLAME_RANGE) {
        s.owed = 0;
        continue;
      }
      s.owed += s.rate * dt * share;
      let n = Math.min(Math.max(3, Math.ceil(s.rate * 0.1)), Math.floor(s.owed));
      s.owed -= Math.floor(s.owed);
      while (n-- > 0) {
        sourcePoint(s, s.k++, this.point);
        const p = this.point;
        this.sink.flames(p.x, (s.y ?? ground(p.x, p.z)) + 0.02, p.z, s.color, 1, 0.12, s.size, s.alpha);
        this.emitted++;
      }
    }
  }

  clear(): void {
    this.live.length = 0;
  }
}

function distanceTo(s: FlameSource, cx: number, cz: number): number {
  if (s.shape !== "faixa") return Math.max(0, Math.hypot(cx - s.x, cz - s.z) - s.radius);
  const dx = s.x2 - s.x;
  const dz = s.z2 - s.z;
  const len2 = dx * dx + dz * dz;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((cx - s.x) * dx + (cz - s.z) * dz) / len2)) : 0;
  return Math.hypot(cx - (s.x + dx * t), cz - (s.z + dz * t));
}
